import User, { USER_STORAGE_KEY_MAP } from 'shared/storage/user';
import { TextField, Select, TextArea } from '@wework/ray-core';

function shouldPersistValue(inputName) {
  return Boolean(USER_STORAGE_KEY_MAP[inputName]);
}

export function storeValuesToPersist(keyValueMap) {
  const valueMapToSave = {};
  Object.keys(keyValueMap).forEach(key => {
    if (shouldPersistValue(key)) {
      valueMapToSave[USER_STORAGE_KEY_MAP[key]] = keyValueMap[key];
    }
  });
  User.saveValues(valueMapToSave);
}

function createRayField(tagName, formEl) {
  switch (tagName) {
    case 'INPUT':
      return TextField.create(formEl);
    case 'SELECT':
      return Select.create(formEl);
    case 'TEXTAREA':
      return TextArea.create(formEl);
    default:
      return undefined;
  }
}

function getInputValueFromDOM(input) {
  if (input.intlPhoneInput) {
    const { dialCode } = input.intlPhoneInput.selectedCountryData;
    const prefixRegex = new RegExp(`^\\+${dialCode}`);
    return input.intlPhoneInput._getFullNumber().replace(prefixRegex, `+${dialCode}-`);
  }

  if (input.getAttribute('type') === 'radio') {
    if (!input.checked) {
      return null;
    }
  }

  if (input.getAttribute('type') === 'checkbox') {
    return input.checked;
  }

  return input.value;
}

export default class LeadForm {
  constructor(form, onSubmit, options = {}) {
    this.form = form;
    this.onSubmit = onSubmit;
    this.formElements = this.form.querySelectorAll('input, select, textarea');
    this.rayFormElementWrappers = this.form.querySelectorAll('.ray-text-field, .ray-select, .ray-text-area');
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.isValid = true;
    this.attachListeners();
    this.prefillRayFormElements();
    this.options = options;
  }

  attachListeners() {
    this.form.addEventListener('submit', this.handleSubmit.bind(this), false);
    Array.from(this.formElements).forEach(input => {
      const boundSetInputValidity = this.setInputValidity.bind(this, input);
      input.addEventListener('blur', boundSetInputValidity);
      input.addEventListener('change', boundSetInputValidity);
    });
  }

  prefillRayFormElements() {
    Array.from(this.rayFormElementWrappers).forEach(formEl => {
      const input = formEl.querySelector('input, select, textarea');
      const name = input && input.name;
      if (!shouldPersistValue(name)) return;

      const storedValue = User.getValue(USER_STORAGE_KEY_MAP[name]);
      if (!storedValue) return;

      const rayFieldEl = createRayField(input.tagName, formEl);
      if (rayFieldEl) {
        rayFieldEl.set(storedValue);
        rayFieldEl.assignClasses();
      }
    });
  }

  async handleSubmit(event) {
    event.preventDefault();

    const formValues = this.getFormValuesAndValidateForm();

    if (!this.isValid) {
      return false;
    }

    storeValuesToPersist(formValues);

    this.disableSubmit();

    try {
      await this.onSubmit(event, formValues);
    } catch (error) {
      // @todo something with this error
      console.error(error); // eslint-disable-line no-console
      throw error;
    } finally {
      this.enableSubmit();
    }
  }

  setInputValidity(input) {
    const isValid = input.checkValidity();
    const invalidClass = 'invalid';
    const classListFunction = isValid ? 'remove' : 'add';
    const isRay = Array.from(input.classList).find(className => className.startsWith('ray-'));
    const isWrappedInput = input.parentElement.classList.contains('ray-text-field__wrapper');
    input.classList[classListFunction](invalidClass);

    if ((input.intlPhoneInput || isWrappedInput) && isRay) {
      input.parentElement.parentElement.classList[classListFunction](invalidClass);
    } else if (input.intlPhoneInput || isRay) {
      input.parentElement.classList[classListFunction](invalidClass);
    }

    this.isValid = this.isValid && isValid;

    const isCheckedRadio = isValid && input.type === 'radio' && input.checked;
    if (isCheckedRadio) {
      const form = input.closest('form');
      const otherRadios = form.querySelectorAll(`[type=radio][name=${input.name}]:not([value=${input.value}])`);
      otherRadios.forEach(radio => this.setInputValidity(radio));
    }
  }

  validate(input) {
    this.setInputValidity(input);
    return getInputValueFromDOM(input);
  }

  disableSubmit() {
    this.submitButton.disabled = true;
  }

  enableSubmit() {
    this.submitButton.disabled = false;
  }

  /**
   * Returns an object that contains all of the formvalues and sets form validity at the same time
   * @returns {any}
   */
  getFormValuesAndValidateForm() {
    this.isValid = true;
    // visibleFormElementsSelector is an option that should be used if the form has hidden fields,
    // by default hidden fields will be validated, so you can pass a custom selector not to include those
    const formElements = this.options.visibleFormElementsSelector
      ? this.form.querySelectorAll(this.options.visibleFormElementsSelector)
      : this.formElements;
    const acc = {};
    for (let i = 0, formElCount = formElements.length; i < formElCount; i++) {
      const formEl = formElements[i];
      const inputValue = this.validate(formEl);
      if (inputValue || inputValue === false) {
        acc[formEl.name] = inputValue;
      }
    }
    return acc;
  }
}
